<template>
  <span class="cd-drill">
    <span :class="valueClasses" @click="onDrill">{{ count | number }}</span>
    <!-- ALWAYS rendered (hidden when zero): the icon reserves the same space
         in every row, so the numbers line up in a clean column instead of
         zero-rows sitting further right than the others. -->
    <feather-icon
      icon="ExternalLinkIcon"
      size="13"
      class="cd-ext"
      :class="{ 'cd-ext--off': !count }"
      v-b-tooltip.hover
      :title="count ? $t('clientDashboard.openInNewTab') : ''"
      @click.stop="count && $emit('external')"
    />
  </span>
</template>

<script>
export default {
  name: 'DrillCell',
  props: {
    count: { default: 0 },
    // When true, render the colored "chip" style (age buckets); otherwise the
    // plain clickable link style used by totals / FTD / STD cells.
    chip: { type: Boolean, default: false },
    variant: { type: String, default: '' }, // good | warn | bad
  },
  computed: {
    valueClasses() {
      if (this.chip) {
        return ['cd-chip', this.count ? `cd-chip--${this.variant}` : 'cd-chip--zero', { 'cd-clickable': this.count }];
      }
      return { 'cd-clickable cd-link': this.count };
    },
  },
  methods: {
    onDrill() {
      if (this.count) this.$emit('drill');
    },
  },
};
</script>

<style scoped lang="scss">
.cd-drill { display: inline-flex; align-items: center; gap: 5px; }
.cd-ext {
  color: #b9b9c3;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease, color 0.15s ease;
}
.cd-drill:hover .cd-ext { opacity: 1; }
.cd-ext:hover { color: #007bff; }
/* zero cells: the icon still RESERVES its width (alignment) but never shows */
.cd-ext--off { visibility: hidden; pointer-events: none; }

/* Value styling — copied from ClientDashboard so the cell renders identically
   (scoped styles there don't reach this component's elements).
   tabular-nums + fixed min-widths + centered/right-aligned digits: 1-, 2- and
   3-digit values occupy the same box, so every row lines up. */
.cd-chip {
  display: inline-block;
  min-width: 44px;
  padding: 2px 9px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.78rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.cd-clickable {
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease;
  &:hover { transform: translateY(-1px); filter: brightness(0.97); }
}
.cd-link {
  color: #007BFF;
  text-decoration-line: underline;
  text-decoration-style: dotted;
  text-underline-offset: 3px;
  font-weight: 700;
  &:hover { color: #0062cc; }
}
/* Plain (non-chip) numbers: the EXACT same box geometry as the chips
   (44px, centered digits) — so subtotal/total rows sit on the same axis as
   the chip rows above them instead of zigzagging. */
.cd-drill > span:not(.cd-chip) {
  display: inline-block;
  min-width: 44px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.cd-chip.cd-clickable:hover { box-shadow: 0 4px 10px -4px rgba(20, 24, 50, 0.3); }
.cd-chip--good { background: rgba(40, 199, 111, 0.14); color: #28a866; }
.cd-chip--warn { background: rgba(245, 159, 11, 0.16); color: #d98a06; }
.cd-chip--bad  { background: rgba(234, 84, 85, 0.14); color: #e0484a; }
.cd-chip--zero { background: transparent; color: #c2c7d6; font-weight: 500; }
</style>

<style scoped lang="scss">
body.dark-layout {
  .cd-chip--zero { color: #5a6377; }
  .cd-link { color: #6cb2ff; &:hover { color: #8fc6ff; } }
}
</style>
